import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearch } from "@tanstack/react-router";
import { CheckCircle2, CircleAlert, Eye, EyeOff, Loader2 } from "lucide-react";
import { MIN_PASSWORD_LENGTH, isValidUsernameFormat, normalizeUsername, USERNAME_FORMAT_HINT } from "@onemileauto/shared-types";
import { Button, Card, Input, Label } from "@onemileauto/ui";
import { AuthApiError, checkUsernameAvailability, getResetTokenInfo, resetPassword } from "../../lib/auth-client";

type UsernameStatus = "idle" | "checking" | "available" | "taken" | "invalid";

// How long to wait after the user stops typing before checking availability
// — long enough to not fire a request per keystroke, short enough that the
// feedback still feels immediate on a form this short.
const USERNAME_CHECK_DEBOUNCE_MS = 400;

/**
 * Consumes a single-use setup/reset token (the `?token=` query param from
 * the emailed link — see apps/mail's lib/auth-mail.ts) and sets a new
 * password. Same page/route handles BOTH "a new user setting up their
 * first password" and "an existing user resetting a forgotten one" — the
 * server tells this page which case it is via GET /api/auth/token-info
 * (`needsUsername`), and only a brand-new/never-set-up user sees the
 * "choose a username" step (Phase 8.1 — the client wants to log in with a
 * plain username, chosen by the user themselves during this exact setup
 * step, not assigned by an owner).
 */
export function ResetPasswordPage() {
  const search = useSearch({ strict: false }) as { token?: string };
  const token = search.token;

  const [tokenCheck, setTokenCheck] = useState<"loading" | "ready" | "invalid">(token ? "loading" : "invalid");
  const [needsUsername, setNeedsUsername] = useState(false);

  const [username, setUsername] = useState("");
  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>("idle");
  const [usernameMessage, setUsernameMessage] = useState<string | null>(null);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Look up whether this link's user still needs to choose a username
  // BEFORE rendering the form, so it never flashes the wrong shape. Doesn't
  // consume the token (see routes/auth.ts's GET /api/auth/token-info) — a
  // network hiccup here just falls back to hiding the username step; the
  // real, authoritative check happens server-side again on submit either
  // way, so nothing security-relevant depends on this succeeding.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    getResetTokenInfo(token)
      .then((info) => {
        if (cancelled) return;
        setNeedsUsername(info.needsUsername);
        setTokenCheck("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof AuthApiError && err.status === 400) {
          setTokenCheck("invalid");
        } else {
          // Network/connectivity issue, not a confirmed-invalid link —
          // proceed without the username step rather than block the whole
          // page; the server re-validates the token for real on submit.
          setTokenCheck("ready");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Debounced live availability check as the user types their desired
  // username. Format is checked instantly, client-side, before ever
  // touching the network (see isValidUsernameFormat — the exact same rule
  // the server enforces, imported from the same shared package so the two
  // can't drift).
  useEffect(() => {
    if (!needsUsername) return;
    const normalized = normalizeUsername(username);
    if (!normalized) {
      setUsernameStatus("idle");
      setUsernameMessage(null);
      return;
    }
    if (!isValidUsernameFormat(normalized)) {
      setUsernameStatus("invalid");
      setUsernameMessage(USERNAME_FORMAT_HINT);
      return;
    }
    setUsernameStatus("checking");
    setUsernameMessage(null);
    const handle = setTimeout(() => {
      checkUsernameAvailability(normalized)
        .then((result) => {
          setUsernameStatus(result.available ? "available" : "taken");
          setUsernameMessage(result.available ? null : (result.detail ?? "That username is already taken."));
        })
        .catch(() => {
          // Rate-limited or unreachable — don't block typing/submission on
          // this best-effort check; the server enforces uniqueness for real
          // on submit regardless.
          setUsernameStatus("idle");
          setUsernameMessage(null);
        });
    }, USERNAME_CHECK_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [username, needsUsername]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    if (needsUsername && usernameStatus !== "available") {
      setError(usernameStatus === "taken" ? "That username is already taken. Choose another." : "Choose a username to continue.");
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword(token!, password, needsUsername ? normalizeUsername(username) : undefined);
      setDone(true);
    } catch (err) {
      if (err instanceof AuthApiError) {
        setError(err.message || "That link is invalid or has expired. Request a new one.");
      } else {
        setError("Couldn't reach the mail server. Check your connection and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!token || tokenCheck === "invalid") {
    return (
      <div className="flex flex-1 items-center justify-center p-4">
        <Card className="w-full max-w-sm p-6 text-center sm:p-8">
          <h1 className="font-display text-2xl text-charcoal">This link isn&rsquo;t valid</h1>
          <p className="mt-2 text-sm text-muted">
            The link you followed is missing its reset token, expired, or has already been used. Request a new one
            below.
          </p>
          <Link to="/forgot-password" className="mt-5 block">
            <Button size="lg" className="w-full">
              Request a new link
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex flex-1 items-center justify-center p-4">
        <Card className="w-full max-w-sm p-6 text-center sm:p-8">
          <h1 className="font-display text-2xl text-charcoal">
            {needsUsername ? "You’re all set" : "Password set"}
          </h1>
          <p className="mt-2 text-sm text-muted">
            {needsUsername ? (
              <>
                Sign in with your new username, <span className="font-medium text-charcoal">{normalizeUsername(username)}</span>, and
                your new password.
              </>
            ) : (
              "You're all set. Sign in with your new password."
            )}
          </p>
          <Link to="/login" className="mt-5 block">
            <Button size="lg" className="w-full">
              Continue to sign in
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  if (tokenCheck === "loading") {
    return (
      <div className="flex flex-1 items-center justify-center p-4">
        <Loader2 size={22} className="animate-spin text-muted" aria-label="Checking your link…" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 items-center justify-center p-4">
      <Card className="w-full max-w-sm p-6 sm:p-8">
        <h1 className="font-display text-2xl text-charcoal sm:text-3xl">
          {needsUsername ? "Set up your account" : "Set a new password"}
        </h1>
        <p className="mt-1.5 text-sm text-muted">
          {needsUsername
            ? "Choose a username and password to sign in with from now on."
            : `Choose a password with at least ${MIN_PASSWORD_LENGTH} characters.`}
        </p>

        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
          {needsUsername && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reset-username">Username</Label>
              <div className="relative">
                <Input
                  id="reset-username"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pr-11"
                  invalid={usernameStatus === "taken" || usernameStatus === "invalid"}
                />
                <span className="absolute inset-y-0 right-0 flex w-11 items-center justify-center">
                  {usernameStatus === "checking" && <Loader2 size={18} className="animate-spin text-muted" />}
                  {usernameStatus === "available" && <CheckCircle2 size={18} className="text-success" />}
                  {(usernameStatus === "taken" || usernameStatus === "invalid") && (
                    <CircleAlert size={18} className="text-danger" />
                  )}
                </span>
              </div>
              <p className={`text-xs ${usernameStatus === "taken" || usernameStatus === "invalid" ? "text-danger" : "text-muted"}`}>
                {usernameMessage ?? USERNAME_FORMAT_HINT}
              </p>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reset-password">{needsUsername ? "Password" : "New password"}</Label>
            <div className="relative">
              <Input
                id="reset-password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pr-11"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-muted hover:text-charcoal"
              >
                {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reset-password-confirm">Confirm password</Label>
            <Input
              id="reset-password-confirm"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          {error && (
            <p role="alert" className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2.5 text-sm text-danger">
              {error}
            </p>
          )}

          <Button type="submit" size="lg" disabled={submitting} className="mt-1 w-full">
            {submitting ? "Setting up…" : needsUsername ? "Create account" : "Set password"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
